import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import * as React from "react"

import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import { RequestFailed } from "@/api/client"
import { useDeleteEmployee, useEmployee } from "@/features/employees/api"
import { EmployeeForm } from "@/features/employees/employee-form"
import { EmployeeProfile } from "@/features/employees/employee-profile"
import { fullName } from "@/features/employees/fields"
import { TrainingHistory } from "@/features/employees/training-history"
import { ConfirmDialog } from "@/components/modal"
import { ActiveBadge } from "@/features/employees/text"
import { useSession } from "@/lib/session"
import { DocumentsPanel } from "@/features/documents/panel"

export const Route = createFileRoute("/employees/$employeeId")({
  component: EmployeeDetailScreen,
})

function EmployeeDetailScreen() {
  const { employeeId } = Route.useParams()
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const employee = useEmployee(Number(employeeId))
  const remove = useDeleteEmployee()
  const [editing, setEditing] = React.useState(false)
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  const missing = employee.error instanceof RequestFailed && employee.error.status === 404

  return (
    <>
      <Link
        to="/employees"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All employees
      </Link>

      {employee.isLoading ? (
        <LoadingProfile />
      ) : missing ? (
        <Card>
          <EmptyState
            title="That employee could not be found"
            description="The record may have been deleted since this link was made."
            action={
              <Link to="/employees">
                <Button variant="secondary">Back to employees</Button>
              </Link>
            }
          />
        </Card>
      ) : employee.isError || !employee.data ? (
        <Card>
          <ErrorState
            description="This employee could not be loaded."
            onRetry={() => void employee.refetch()}
          />
        </Card>
      ) : (
        <>
          <PageHeader
            eyebrow={`${employee.data.employee_number}${employee.data.job_title ? ` · ${employee.data.job_title}` : ""}`}
            title={fullName(employee.data)}
            actions={
              <div className="flex items-center gap-2">
                <ActiveBadge active={employee.data.active} />
                {canWrite && (
                  <>
                    <Button variant="secondary" onClick={() => setEditing(true)}>
                      <Pencil className="h-4 w-4" aria-hidden />
                      Edit
                    </Button>
                    {/* Deleting is rare and deliberate: the normal end of
                        someone's employment is the inactive flag, which
                        keeps their history. */}
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setDeleteError(null)
                        setConfirmingDelete(true)
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            }
          />

          <EmployeeProfile employee={employee.data} />

          {/* Above the attachments deliberately: what this person is trained
              to do is the question the page is opened to answer, and it was
              the top gap the 2026-07-30 walkthrough found. */}
          <TrainingHistory employeeId={Number(employeeId)} />

          <div className="mt-6">
            <DocumentsPanel parentType="employees" parentId={Number(employeeId)} />
          </div>

          {editing && (
            <EmployeeForm
              employee={employee.data}
              onClose={() => setEditing(false)}
              onSaved={() => setEditing(false)}
            />
          )}

          {confirmingDelete && (
            <ConfirmDialog
              title={`Delete ${fullName(employee.data)}?`}
              confirmLabel="Delete permanently"
              onClose={() => setConfirmingDelete(false)}
              onConfirm={() => {
                const id = employee.data?.id
                if (id == null) return
                setDeleteError(null)
                remove.mutate(id, {
                  onSuccess: () => void navigate({ to: "/employees" }),
                  onError: () =>
                    setDeleteError(
                      "That employee could not be deleted. Anyone with training records is kept as audit evidence: mark them inactive instead.",
                    ),
                })
              }}
              pending={remove.isPending}
              error={deleteError}
            >
              <p className="text-sm">
                This cannot be undone. An employee who has left should be marked
                inactive instead, which keeps their training history for SETA and
                BBBEE reporting.
              </p>
            </ConfirmDialog>
          )}
        </>
      )}
    </>
  )
}

function LoadingProfile() {
  return (
    <div className="mt-2" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading employee</span>
      <Skeleton className="h-7 w-56" />
      <Skeleton className="mt-2 h-4 w-72" />
      <div className="mt-6 grid gap-4">
        {[0, 1, 2].map((card) => (
          <Card key={card}>
            <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((row) => (
                <div key={row}>
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="mt-2 h-4 w-32" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
